import * as React from "react"
import {
  Building2,
  BarChart3,
  Settings,
  FlaskConical,
  Home,
  LogOut,
  ChevronRight,
  ChevronsUpDown,
  Moon,
  Sun,
} from "lucide-react"
import { Link, useLocation } from "react-router-dom"
import { useAuth0 } from "@auth0/auth0-react"
import { useTheme } from "next-themes"
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarMenuSub,
  SidebarMenuSubButton,
  SidebarMenuSubItem,
  useSidebar,
} from "@/components/ui/sidebar"
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuTrigger,
} from "@/components/_components/dropdown-menu"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/_components/avatar"
import { NAV_GROUPS } from "@/components/navigation/nav-config"
import { ROUTES } from "@/constants/routes"
import { getUserInitials, getUsername } from "@/lib/userUtils"
import logoImage from "@/assets/images/service-global-logo.png"
import { useAuthorities } from "@/hooks/useAuthorities"

const menuItems = [
  {
    title: "Home",
    url: ROUTES.HOME,
    icon: Home,
    hasSubmenu: false,
  },
  {
    title: "Campaigns",
    items: NAV_GROUPS.find(g => g.label === "Campaigns")?.items || [],
    icon: Building2,
    hasSubmenu: true,
  },
  {
    title: "Analytics",
    items: NAV_GROUPS.find(g => g.label === "Analytics")?.items || [],
    icon: BarChart3,
    hasSubmenu: true,
  },
  {
    title: "Admin Center",
    items: NAV_GROUPS.find(g => g.label === "Admin Center")?.items || [],
    icon: Settings,
    hasSubmenu: true,
  },
  {
    title: "Testing",
    items: NAV_GROUPS.find(g => g.label === "Testing")?.items || [],
    icon: FlaskConical,
    hasSubmenu: true,
  },
]

export function AppSidebar() {
  const location = useLocation()
  const { user, logout } = useAuth0()
  const { theme, setTheme } = useTheme()
  const { hasAuthority, hasAnyAuthority, hasAllAuthorities } = useAuthorities()
  const userInitials = getUserInitials(user)
  const username = getUsername(user)
  const [expandedItems, setExpandedItems] = React.useState<Set<string>>(new Set())
  const [mounted, setMounted] = React.useState(false)
  const { state, isMobile } = useSidebar()

  // Avoid hydration mismatch
  React.useEffect(() => {
    setMounted(true)
  }, [])

  // Helper function to check if user has permission for a nav item
  const hasPermissionForItem = React.useCallback((item: typeof NAV_GROUPS[0]['items'][0]) => {
    // If no authority is specified, item is always visible
    if (!item.authority && !item.anyOf && !item.allOf) {
      return true
    }

    // Check single authority
    if (item.authority) {
      return hasAuthority(item.authority)
    }

    // Check anyOf authorities
    if (item.anyOf && item.anyOf.length > 0) {
      return hasAnyAuthority(...item.anyOf)
    }

    // Check allOf authorities
    if (item.allOf && item.allOf.length > 0) {
      return hasAllAuthorities(...item.allOf)
    }

    return false
  }, [hasAuthority, hasAnyAuthority, hasAllAuthorities])

  // Filter menu items based on authorities
  const filteredMenuItems = React.useMemo(() => {
    return menuItems.map(item => {
      if (!item.items) {
        // Simple item without submenu
        return { ...item, hidden: false }
      }

      // Filter submenu items based on authorities
      const filteredItems = item.items.filter(hasPermissionForItem)

      return {
        ...item,
        items: filteredItems,
        // Hide the entire group if no items are visible
        hidden: filteredItems.length === 0
      }
    }).filter(item => !item.hidden)
  }, [hasPermissionForItem])

  const handleLogout = () => {
    logout({
      logoutParams: {
        returnTo: window.location.origin,
      },
    })
  }

  const toggleExpanded = (title: string) => {
    setExpandedItems((prev) => {
      const next = new Set(prev)
      if (next.has(title)) {
        next.delete(title)
      } else {
        next.add(title)
      }
      return next
    })
  }

  // Check if any submenu item is active
  const isSubmenuActive = (items: typeof NAV_GROUPS[0]['items']) => {
    return items?.some((item) => location.pathname === item.route) || false
  }

  return (
    <Sidebar collapsible="icon" className="border-r [&_[data-sidebar=sidebar]]:bg-background">
      {/* Logo/Brand Section - Sticky at top */}
      <SidebarHeader className="sticky top-0 z-10 border-b bg-background min-h-[3.5rem] flex items-center justify-center group-data-[collapsible=icon]:justify-center">
        <SidebarGroup>
          <SidebarGroupLabel asChild>
            <Tooltip>
              <TooltipTrigger asChild>
                <Link to={ROUTES.HOME} className="flex items-center justify-center gap-2 px-2 h-full w-full group-data-[collapsible=icon]:px-0 group-data-[collapsible=icon]:justify-center">
                  {/* Full logo when expanded */}
                  <img 
                    src={logoImage} 
                    alt="Service Global" 
                    className="h-8 w-auto object-contain group-data-[collapsible=icon]:hidden"
                  />
                  {/* Favicon icon when collapsed */}
                  <img 
                    src="/engage-ai.svg" 
                    alt="Engage AI" 
                    className="hidden group-data-[collapsible=icon]:flex h-8 w-8 object-contain mx-auto"
                  />
                  <span className="font-semibold text-sidebar-foreground group-data-[collapsible=icon]:hidden">
                    Engage AI
                  </span>
                </Link>
              </TooltipTrigger>
              <TooltipContent
                side="right"
                align="center"
                hidden={state !== "collapsed" || isMobile}
              >
                Home
              </TooltipContent>
            </Tooltip>
          </SidebarGroupLabel>
        </SidebarGroup>
      </SidebarHeader>

      <SidebarContent>
        {/* Main Navigation */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              {filteredMenuItems.map((item) => (
                <React.Fragment key={item.title}>
                  {item.url ? (
                    // Simple link item (no submenu)
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        asChild
                        isActive={location.pathname === item.url}
                        tooltip={item.title}
                      >
                        <Link to={item.url}>
                          <item.icon />
                          <span>{item.title}</span>
                        </Link>
                      </SidebarMenuButton>
                    </SidebarMenuItem>
                  ) : (
                    // Item with submenu
                    <SidebarMenuItem>
                      <SidebarMenuButton
                        onClick={() => toggleExpanded(item.title)}
                        isActive={isSubmenuActive(item.items)}
                        tooltip={item.title}
                        className="w-full"
                      >
                        <item.icon />
                        <span>{item.title}</span>
                        <ChevronRight
                          className={`ml-auto h-4 w-4 transition-transform duration-200 ${
                            expandedItems.has(item.title) ? "rotate-90" : ""
                          }`}
                        />
                      </SidebarMenuButton>
                      {expandedItems.has(item.title) && item.items && (
                        <SidebarMenuSub>
                          {item.items.map((navItem) => (
                            <SidebarMenuSubItem key={navItem.route}>
                              <Tooltip>
                                <TooltipTrigger asChild>
                                  <SidebarMenuSubButton
                                    asChild
                                    isActive={location.pathname === navItem.route}
                                  >
                                    <Link to={navItem.route}>
                                      <span>{navItem.label}</span>
                                    </Link>
                                  </SidebarMenuSubButton>
                                </TooltipTrigger>
                                <TooltipContent
                                  side="right"
                                  align="center"
                                  hidden={state !== "collapsed" || isMobile}
                                >
                                  {navItem.label}
                                </TooltipContent>
                              </Tooltip>
                            </SidebarMenuSubItem>
                          ))}
                        </SidebarMenuSub>
                      )}
                    </SidebarMenuItem>
                  )}
                </React.Fragment>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

      </SidebarContent>

      {/* User Profile Footer - Sticky at bottom */}
      <SidebarFooter className="sticky bottom-0 mt-auto border-t bg-background">
        <SidebarMenu>
          <SidebarMenuItem>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <SidebarMenuButton
                  size="lg"
                  tooltip={username || "User Profile"}
                  className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground w-full"
                >
                  <Avatar className="h-8 w-8 rounded-full">
                    <AvatarImage src={user?.picture} alt={user?.name || "User"} />
                    <AvatarFallback className="rounded-full bg-primary text-primary-foreground">
                      {userInitials}
                    </AvatarFallback>
                  </Avatar>
                  <div className="grid flex-1 text-left text-sm leading-tight group-data-[collapsible=icon]:hidden">
                    <span className="truncate font-semibold">
                      {username}
                    </span>
                    <span className="truncate text-xs text-muted-foreground">
                      {user?.email || ""}
                    </span>
                  </div>
                  <ChevronsUpDown className="ml-auto h-4 w-4 group-data-[collapsible=icon]:hidden" />
                </SidebarMenuButton>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                className="w-[--radix-dropdown-menu-trigger-width] min-w-56 rounded-lg mb-2"
                side="right"
                align="start"
                sideOffset={8}
              >
                <DropdownMenuLabel className="p-0 font-normal cursor-pointer">
                  <div className="flex items-center gap-2 px-1 py-1.5 text-left text-sm">
                    <Avatar className="h-8 w-8 rounded-full">
                      <AvatarImage src={user?.picture} alt={user?.name || "User"} />
                      <AvatarFallback className="rounded-full bg-primary text-primary-foreground">
                        {userInitials}
                      </AvatarFallback>
                    </Avatar>
                    <div className="grid flex-1 text-left text-sm leading-tight">
                      <span className="truncate font-semibold">
                        {username}
                      </span>
                      <span className="truncate text-xs text-muted-foreground">
                        {user?.email || ""}
                      </span>
                    </div>
                  </div>
                </DropdownMenuLabel>
                {/* <DropdownMenuItem className="cursor-pointer">
                  <Bell className="mr-2 h-4 w-4" />
                  Notifications
                </DropdownMenuItem> */}
                {mounted && (
                  <DropdownMenuItem 
                    onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
                    className="cursor-pointer"
                  >
                    {theme === "dark" ? (
                      <Sun className="mr-2 h-4 w-4" />
                    ) : (
                      <Moon className="mr-2 h-4 w-4" />
                    )}
                    {theme === "dark" ? "Light Mode" : "Dark Mode"}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem onClick={handleLogout} className="cursor-pointer">
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>
    </Sidebar>
  )
}
